// Imports
import { Plugin } from '@ckeditor/ckeditor5-core';
import type { ViewElement } from '@ckeditor/ckeditor5-engine';


// CSS class names internal to @ckeditor/ckeditor5-html-embed's DOM structure
//  -> These are not part of a public API; verify they still match on CKEditor upgrades
const RAW_HTML_EMBED_SOURCE_CLASS = 'raw-html-embed__source';
const RAW_HTML_EMBED_WIDGET_CLASS = 'raw-html-embed';
const RAW_HTML_EMBED_SAVE_BUTTON_CLASS = 'raw-html-embed__save-button';


// CSS class names internal to @ckeditor/ckeditor5-widget's DOM structure
//  -> These are not part of a public API; verify they still match on CKEditor upgrades
const WIDGET_TYPE_AROUND_BUTTON_CLASS = 'ck-widget__type-around__button';
const WIDGET_TYPE_AROUND_BUTTON_BEFORE_CLASS = 'ck-widget__type-around__button_before';


// Plugin
export default class HTMLSnippetAutoSave extends Plugin {
	// Handle plugin initialization
	init() {
		// Initialize
		const editor = this.editor;
		
		
		// Wait for the editor UI to be ready
		editor.ui.on('ready', () => {
			// Get DOM root
			const domRoot = editor.editing.view.getDomRoot();
			
			
			// Skip if no DOM root
			if (!domRoot) {
				return;
			}
			
			
			// Initialize
			let mousedownTarget: Element | null = null;
			let savedOnMousedown = false;
			let typeAroundHandledInPointerdown = false;
			let suppressNextClick = false;
			let cancelInProgress = false;
			
			
			// Saves the active widget and handles "insert paragraph" button clicks
			this.listenTo(
				editor.editing.view.document,
				'pointerdown',
				(evt, domEventData: { domTarget: Element; domEvent: MouseEvent }) => {
					// Reset
					mousedownTarget = null;
					savedOnMousedown = false;
					typeAroundHandledInPointerdown = false;
					suppressNextClick = false;
					cancelInProgress = false;
					
					
					// Destructure
					const { domTarget, domEvent: mouseEvent } = domEventData;
					
					
					// Return early
					if (!(domTarget instanceof Element)) {
						return;
					}
					
					
					// Set target
					mousedownTarget = domTarget;
					
					
					// Find the active textarea
					const editingTextarea = domRoot.querySelector<HTMLTextAreaElement>(
						`.${RAW_HTML_EMBED_SOURCE_CLASS}:not(:disabled)`
					);
					
					
					// Skip if not found
					if (!editingTextarea) {
						return;
					}
					
					
					// Find the textarea's widget
					const activeWidget = editingTextarea.closest<HTMLElement>(`.${RAW_HTML_EMBED_WIDGET_CLASS}`);
					
					
					// Skip if not found
					if (!activeWidget) {
						return;
					}
					
					
					// Find the "insert paragraph" button
					const typeAroundButton = domTarget.closest<HTMLElement>(`.${WIDGET_TYPE_AROUND_BUTTON_CLASS}`);
					
					
					// Find the widget that has the button
					const targetWidget = typeAroundButton?.closest<HTMLElement>(`.${RAW_HTML_EMBED_WIDGET_CLASS}`) ?? null;
					
					
					// Determine the target widget's model element before saving
					let widgetModelElement = null;
					
					if (typeAroundButton && targetWidget) {
						widgetModelElement =
							targetWidget === activeWidget
								? editor.model.document.selection.getSelectedElement()
								: (() => {
										const viewElement = editor.editing.view.domConverter.mapDomToView(targetWidget);
										return viewElement?.is('element')
											? (editor.editing.mapper.toModelElement(viewElement as ViewElement) ?? null)
											: null;
									})();
					}
					
					
					// Save the active widget
					const clickOutsideActiveWidget = !activeWidget.contains(domTarget);
					
					if (clickOutsideActiveWidget || widgetModelElement) {
						savedOnMousedown = true;
						activeWidget.querySelector<HTMLButtonElement>(`.${RAW_HTML_EMBED_SAVE_BUTTON_CLASS}`)?.click();
					}
					
					
					// Handle if not inserting paragraph
					if (!typeAroundButton || !widgetModelElement) {
						if (!clickOutsideActiveWidget) {
							cancelInProgress = true;
						}
						
						return;
					}
					
					
					// Insert the new paragraph
					const insertPosition = typeAroundButton.classList.contains(WIDGET_TYPE_AROUND_BUTTON_BEFORE_CLASS)
						? 'before'
						: 'after';
					
					const attributesToCopy = editor.model.schema.getAttributesWithProperty(
						widgetModelElement,
						'copyOnReplace',
						true
					);
					
					typeAroundHandledInPointerdown = true;
					suppressNextClick = true;
					editor.execute('insertParagraph', {
						position: editor.model.createPositionAt(widgetModelElement, insertPosition),
						attributes: attributesToCopy,
					});
					
					editor.editing.view.focus();
					
					
					// Stop so this isn't duplicated
					mouseEvent.preventDefault();
					evt.stop();
				},
				{ priority: 'highest' }
			);
			
			
			// Suppresses clicks following a paragraph insertion
			this.listenTo(
				editor.editing.view.document,
				'click',
				(evt) => {
					if (suppressNextClick) {
						suppressNextClick = false;
						evt.stop();
					}
				},
				{ priority: 'highest' }
			);
			
			
			// Fallback save for outside-widget clicks
			this.listenTo(
				editor.editing.view.document,
				'mousedown',
				(evt, domEventData: { domTarget: Element }) => {
					// Reset and skip if already handled
					if (typeAroundHandledInPointerdown) {
						typeAroundHandledInPointerdown = false;
						evt.stop();
						return;
					}
					
					
					// Reset
					mousedownTarget = null;
					savedOnMousedown = false;
					
					
					// Destructure
					const { domTarget } = domEventData;
					
					
					// Return early
					if (!(domTarget instanceof Element)) {
						return;
					}
					
					
					// Set target
					mousedownTarget = domTarget;
					
					
					// Find the active textarea
					const editingTextarea = domRoot.querySelector<HTMLTextAreaElement>(
						`.${RAW_HTML_EMBED_SOURCE_CLASS}:not(:disabled)`
					);
					
					
					// Skip if not found
					if (!editingTextarea) {
						return;
					}
					
					
					// Find the textarea's widget
					const activeWidget = editingTextarea.closest<HTMLElement>(`.${RAW_HTML_EMBED_WIDGET_CLASS}`);
					
					
					// Skip if not found or if clicking inside the active widget
					if (!activeWidget || activeWidget.contains(domTarget)) {
						return;
					}
					
					
					// Click the save button
					const saveButton = activeWidget.querySelector<HTMLButtonElement>(`.${RAW_HTML_EMBED_SAVE_BUTTON_CLASS}`);
					
					if (saveButton) {
						savedOnMousedown = true;
						saveButton.click();
					}
				},
				{ priority: 'high' }
			);
			
			
			// Detects inside-widget button clicks before propagation stops
			domRoot.addEventListener(
				'pointerdown',
				(event) => {
					// Get the event target
					const target = event.target;
					
					if (!(target instanceof Element)) {
						return;
					}
					
					
					// Find the active textarea
					const editingTextarea = domRoot.querySelector<HTMLTextAreaElement>(
						`.${RAW_HTML_EMBED_SOURCE_CLASS}:not(:disabled)`
					);
					
					
					// Skip if not found
					if (!editingTextarea) {
						return;
					}
					
					
					// Find the textarea's widget
					const activeWidget = editingTextarea.closest<HTMLElement>(`.${RAW_HTML_EMBED_WIDGET_CLASS}`);
					
					
					// Set flag when clicking inside the active widget so `focusout` skips save
					if (activeWidget?.contains(target)) {
						cancelInProgress = true;
					}
				},
				{ capture: true }
			);
			
			
			// Auto-saves when focus leaves the textarea
			domRoot.addEventListener('focusout', (event) => {
				// Get the event target
				const target = event.target;
				
				if (!(target instanceof HTMLElement)) {
					return;
				}
				
				
				// Skip if not an HTML snippet textarea
				if (!target.classList.contains(RAW_HTML_EMBED_SOURCE_CLASS)) {
					return;
				}
				
				
				// Find the parent widget
				const parentWidget = target.closest<HTMLElement>(`.${RAW_HTML_EMBED_WIDGET_CLASS}`);
				
				
				// Skip if no parent widget found
				if (!parentWidget) {
					return;
				}
				
				
				// Skip if the mousedown landed inside the widget
				//  -> Relies on `mousedown` firing before `focusout`; for keyboard focus changes, `mousedownTarget` retains its previous value so this check is best-effort
				if (mousedownTarget && mousedownTarget !== target && parentWidget.contains(mousedownTarget)) {
					return;
				}
				
				
				// Skip if cancel is in progress or already saved on mousedown
				if (cancelInProgress || savedOnMousedown) {
					return;
				}
				
				
				// Defer to avoid a CKEditor error from modifying the view during event handling
				queueMicrotask(() => {
					// Skip if the target was removed from the DOM before the microtask ran
					if (!target.isConnected) {
						return;
					}
					
					
					// Click the widget's save button
					parentWidget.querySelector<HTMLButtonElement>(`.${RAW_HTML_EMBED_SAVE_BUTTON_CLASS}`)?.click();
				});
				
				
				// Reset stale mousedown state so keyboard-triggered focus changes aren't affected
				mousedownTarget = null;
			});
		});
	}
	
	
	// The name this plugin can be referred to by
	public static get pluginName(): 'HTMLSnippetAutoSave' {
		return 'HTMLSnippetAutoSave';
	}
}
